import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { Sparkles, X } from "lucide-react";
import { CONGRATS_MESSAGES, BONUS_MESSAGES, BADGES, ARCHETYPES } from "../lib/constants";
import { playCelebrationSound } from "../lib/sound";

const pickMessage = () => CONGRATS_MESSAGES[Math.floor(Math.random() * CONGRATS_MESSAGES.length)];

export default function RewardOverlay({ open, onClose, result, archetypeKey = "blue" }) {
  const message = useRef(pickMessage());
  const archetype = ARCHETYPES[archetypeKey] || ARCHETYPES.blue;

  useEffect(() => {
    if (!open) return;
    message.current = pickMessage();
    playCelebrationSound();
    const colors = ["#f68b1f", "#ffd400", "#7ac143", "#0091be"];
    const end = Date.now() + 1200;
    (function frame() {
      confetti({ particleCount: 6, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 6, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 120, spread: 90, origin: { y: 0.6 }, colors });
  }, [open]);

  const events = result?.events || [];
  const totalPoints = result?.points_awarded_now || 0;
  const newBadges = result?.new_badges || [];
  const wasFirst = result?.was_first_submit;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="reward-overlay"
        >
          <motion.div
            initial={{ scale: 0.85, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 170, damping: 18 }}
            className="relative bg-white rounded-[2rem] w-full max-w-md p-8 shadow-2xl overflow-hidden"
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="reward-close-button">
              <X size={20} />
            </button>

            <div className="flex items-center justify-center mb-4">
              <motion.div
                initial={{ rotate: -8, scale: 0.6 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 180, damping: 12, delay: 0.15 }}
                className="relative w-28 h-28 rounded-3xl flex items-center justify-center shadow-xl"
                style={{ background: archetype.color }}
              >
                <Sparkles size={42} className="text-white drop-shadow" />
                <motion.div
                  className="absolute inset-0 rounded-3xl"
                  style={{ boxShadow: `0 0 0 8px ${archetype.color}33` }}
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
              </motion.div>
            </div>

            <div className="text-center">
              {wasFirst && (
                <div className="tc-pill inline-block bg-orange-50 text-orange-700 mb-2 px-3 py-1">FIRST STORY · BIG DEAL</div>
              )}
              <h2 className="font-display text-3xl text-tc-ink" data-testid="reward-title">{message.current.title}</h2>
              <p className="text-slate-500 mt-2" data-testid="reward-body">{message.current.body}</p>
            </div>

            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.25, type: "spring" }}
              className="mt-6 text-center"
            >
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">Points earned</div>
              <div className="font-display text-6xl text-tc-ink" data-testid="reward-points">+{totalPoints}</div>
            </motion.div>

            {events.length > 0 && (
              <div className="mt-5 grid gap-2">
                {events.map((e, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.35 + idx * 0.08 }}
                    className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2"
                  >
                    <span className="text-sm font-bold text-slate-700">{BONUS_MESSAGES[e.kind] || e.kind}</span>
                    <span className="text-sm font-black" style={{ color: archetype.color }}>+{e.amount}</span>
                  </motion.div>
                ))}
              </div>
            )}

            {newBadges.length > 0 && (
              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2 text-center">Badges unlocked</div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {newBadges.map((b) => (
                    <motion.div
                      key={b}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.55 }}
                      className="tc-pill"
                      style={{ background: BADGES[b]?.color || "#083050", color: "#fff" }}
                      data-testid={`reward-badge-${b}`}
                    >
                      <Sparkles size={12} /> {BADGES[b]?.label || b}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={onClose}
              data-testid="reward-continue-button"
              className="mt-7 w-full h-12 rounded-2xl font-bold text-white shadow-md hover:-translate-y-0.5 transition-all"
              style={{ background: "#083050" }}
            >
              Continue
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
